import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { internalOrdersService } from "../../services/internal-orders.service";
import { Check, X, User, Clock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function CheckInPage() {
  const [clientCode, setClientCode] = useState("");
  const [notes, setNotes] = useState("");
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
    clientName?: string;
    orderNumber?: string;
  } | null>(null);

  const checkInMutation = useMutation({
    mutationFn: (data: { clientCode: string; notes?: string }) =>
      internalOrdersService.checkIn(data),
    onSuccess: (data) => {
      setFeedback({
        type: "success",
        message: "Check-in realizado com sucesso!",
        clientName: data.client?.name || "Cliente",
        orderNumber: data.internalOrder?.orderNumber,
      });
      setClientCode("");
      setNotes("");

      // Clear feedback after 5 seconds
      setTimeout(() => setFeedback(null), 5000);
    },
    onError: (error: any) => {
      setFeedback({
        type: "error",
        message: error.response?.data?.message || "Erro ao realizar check-in. Cliente não encontrado.",
      });

      // Clear feedback after 4 seconds
      setTimeout(() => setFeedback(null), 4000);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!clientCode.trim()) {
      setFeedback({
        type: "error",
        message: "Por favor, informe o código do cliente",
      });
      setTimeout(() => setFeedback(null), 3000);
      return;
    }

    checkInMutation.mutate({ clientCode: clientCode.trim(), notes: notes.trim() || undefined });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-smoke via-brand-ink to-brand-smoke flex flex-col">
      {/* Header */}
      <div className="bg-brand-ink/80 backdrop-blur-sm border-b border-brand-gold/20 px-8 py-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-brand-gold/20 flex items-center justify-center">
            <User className="w-6 h-6 text-brand-gold" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-brand-gold font-serif">Check-in de Clientes</h1>
            <p className="text-brand-ivory/60 mt-1">Digite o código do cliente para registrar a entrada</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-2xl">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Client Code Input */}
            <div>
              <label className="block text-2xl font-semibold text-brand-gold mb-4">
                Código do Cliente
              </label>
              <input
                type="text"
                value={clientCode}
                onChange={(e) => setClientCode(e.target.value)}
                placeholder="Digite ID, CPF/CNPJ ou e-mail"
                className="w-full px-8 py-6 text-3xl bg-brand-smoke/50 border-2 border-brand-gold/30 rounded-xl text-brand-ivory placeholder:text-brand-ivory/40 focus:outline-none focus:border-brand-gold transition-colors"
                style={{
                  colorScheme: 'dark',
                  color: 'rgb(244, 241, 233) !important' as any,
                  WebkitTextFillColor: 'rgb(244, 241, 233)',
                }}
                autoFocus
                disabled={checkInMutation.isPending}
              />
            </div>

            {/* Notes Input */}
            <div>
              <label className="block text-xl font-semibold text-brand-gold mb-3">
                Observações (opcional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Adicione observações sobre o check-in..."
                rows={3}
                className="w-full px-6 py-4 text-lg bg-brand-smoke/50 border-2 border-brand-gold/30 rounded-xl text-brand-ivory placeholder:text-brand-ivory/40 focus:outline-none focus:border-brand-gold transition-colors resize-none"
                style={{
                  colorScheme: 'dark',
                  color: 'rgb(244, 241, 233) !important' as any,
                  WebkitTextFillColor: 'rgb(244, 241, 233)',
                }}
                disabled={checkInMutation.isPending}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={checkInMutation.isPending}
              className="w-full py-8 bg-brand-gold hover:bg-brand-gold/90 disabled:bg-brand-ivory/20 disabled:cursor-not-allowed text-brand-ink font-bold text-2xl rounded-xl transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-brand-gold/20"
            >
              {checkInMutation.isPending ? (
                <span className="flex items-center justify-center gap-3">
                  <div className="w-6 h-6 border-3 border-brand-ink border-t-transparent rounded-full animate-spin" />
                  Processando...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-3">
                  <Clock className="w-7 h-7" />
                  Registrar Check-in
                </span>
              )}
            </button>
          </form>

          {/* Feedback Messages */}
          <AnimatePresence>
            {feedback && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className={`mt-8 p-6 rounded-xl border-2 ${
                  feedback.type === "success"
                    ? "bg-emerald-500/10 border-emerald-500/30"
                    : "bg-red-500/10 border-red-500/30"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      feedback.type === "success" ? "bg-emerald-500/20" : "bg-red-500/20"
                    }`}
                  >
                    {feedback.type === "success" ? (
                      <Check className="w-6 h-6 text-emerald-400" />
                    ) : (
                      <X className="w-6 h-6 text-red-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    {feedback.type === "success" && feedback.clientName && (
                      <>
                        <h3 className="text-2xl font-bold text-emerald-400 mb-2">
                          Bem-vindo(a), {feedback.clientName}!
                        </h3>
                        {feedback.orderNumber && (
                          <p className="text-lg text-brand-gold mb-2">
                            Comanda #{feedback.orderNumber} criada automaticamente
                          </p>
                        )}
                      </>
                    )}
                    <p
                      className={`text-lg ${
                        feedback.type === "success" ? "text-emerald-300" : "text-red-300"
                      }`}
                    >
                      {feedback.message}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Instructions */}
          <div className="mt-12 p-6 bg-brand-ink/50 rounded-xl border border-brand-gold/20">
            <h3 className="text-lg font-semibold text-brand-gold mb-3">Instruções</h3>
            <ul className="space-y-2 text-brand-ivory/60">
              <li className="flex items-start gap-2">
                <span className="text-brand-gold mt-1">•</span>
                <span>Digite o ID, CPF/CNPJ ou e-mail do cliente no campo acima</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand-gold mt-1">•</span>
                <span>Adicione observações se necessário (opcional)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand-gold mt-1">•</span>
                <span>Clique em "Registrar Check-in" ou pressione Enter</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand-gold mt-1">•</span>
                <span>O sistema confirmará automaticamente após o registro</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-brand-ink/80 backdrop-blur-sm border-t border-brand-gold/20 px-8 py-4">
        <p className="text-center text-brand-ivory/50 text-sm">
          Sistema de Check-in - Caruso CRM
        </p>
      </div>
    </div>
  );
}
